import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ATTENDANCE_STATUS, classifyDawdle, type AttendanceStatus } from '@projektarbeit/shared';
import { ApiError } from '@/lib/api-client';
import { clearAllClientState } from '@/lib/logout-cleanup';
import { markUserInteracted, playSound } from '@/lib/kiosk-audio';
import { LoadingState } from '@/components/LoadingState';
import { BarcodeScanner, type ScannerFeedback } from '@/components/BarcodeScanner';
import { ScanResultOverlay } from '@/components/ScanResultOverlay';
import { OptimisticOverlay } from '@/components/OptimisticOverlay';
import { OfflineBanner } from '@/components/OfflineBanner';
import { PaCountdown } from '@/components/PaCountdown';
import {
  DEFAULT_DAWDLE_SETTINGS,
  clearKioskToken,
  enqueueOfflineScan,
  getKioskToken,
  isOnlineNow,
  lookupBarcodeCache,
  notifyQueueChanged,
  useKioskMe,
  usePublicSettings,
  useScanMutation,
  useScanQueueSync,
  useStudentCachePrefill,
  type ScanResponse,
} from '@/hooks/useKiosk';
import { usePWAUpdate } from '@/hooks/usePWAUpdate';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { cn } from '@/lib/utils';

const ERROR_OVERLAY_MS = 4000;
const ACTIVITY_LIMIT = 8;

interface KioskError {
  kind: 'STUDENT_NOT_FOUND' | 'RATE_LIMITED' | 'SERVER' | 'NETWORK' | 'OTHER';
  message: string;
}

interface ActivityItem {
  id: string;
  firstName: string;
  lastName: string;
  className: string;
  time: string;
  status: AttendanceStatus | 'auto-logout' | 'logout';
  action: ScanResponse['action'];
}

function formatClock(d: Date): string {
  return d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
}

function formatDate(d: Date): string {
  return d.toLocaleDateString('de-DE', {
    weekday: 'short',
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

function activityTone(item: ActivityItem): {
  iconBg: string;
  iconColor: string;
  symbol: string;
} {
  if (item.action === 'check-out' || item.action === 'still-checked-out') {
    return { iconBg: 'bg-primary/15', iconColor: 'text-primary', symbol: '↩' };
  }
  if (item.status === ATTENDANCE_STATUS.VERSPAETET) {
    return {
      iconBg: 'bg-status-late/15',
      iconColor: 'text-status-late',
      symbol: '⏰',
    };
  }
  return {
    iconBg: 'bg-status-present/15',
    iconColor: 'text-status-present',
    symbol: '✓',
  };
}

/**
 * Kiosk-View (CLAUDE.md §22, design mockup /docs/design/).
 *
 * Drei-Spalten-Layout im Landscape: kompakte Scanner-Frame links, Status-Text
 * (Idle / Erfolg / Fehler / Offline) mittig, „Letzte Anmeldungen" rechts.
 * Header zeigt Schul-Logo, Raum-Name, Online-Status und Uhrzeit. Das gesamte
 * Layout passt ohne Scroll in 100vh — kritisch für die schul-iPads.
 */
export function KioskPage(): JSX.Element {
  useDocumentTitle('Scanner · PA-Tracker');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(() => getKioskToken());
  const [result, setResult] = useState<ScanResponse | null>(null);
  const [error, setError] = useState<KioskError | null>(null);
  const lastScanRef = useRef<{ text: string; at: number } | null>(null);

  const settings = usePublicSettings();
  const me = useKioskMe(token);
  const scanMutation = useScanMutation();
  const [optimistic, setOptimistic] = useState<{
    fullName: string;
    mode: 'online' | 'offline';
  } | null>(null);
  const [activity, setActivity] = useState<ActivityItem[]>([]);

  // Live clock (re-render once per second is cheap; the render budget here is
  // dominated by camera frames anyway).
  const [now, setNow] = useState<Date>(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1_000);
    return () => clearInterval(id);
  }, []);

  // Online indicator — local state mirrors `navigator.onLine` so the header
  // can show the green/red dot without subscribing to TanStack Query.
  const [online, setOnline] = useState<boolean>(() => isOnlineNow());
  useEffect(() => {
    const handle = (): void => setOnline(isOnlineNow());
    window.addEventListener('online', handle);
    window.addEventListener('offline', handle);
    return () => {
      window.removeEventListener('online', handle);
      window.removeEventListener('offline', handle);
    };
  }, []);

  // Background-sync loop + cache pre-fill — both no-ops without a token.
  useScanQueueSync();
  useStudentCachePrefill(token);

  // PWA-Update-Lifecycle (CLAUDE.md §5).
  usePWAUpdate();

  // Token check + 401 recovery — redirect to setup.
  useEffect(() => {
    if (!token) {
      navigate('/kiosk/setup', { replace: true });
    }
  }, [token, navigate]);

  // /kiosk/me failures — typically 401 means the token was revoked.
  useEffect(() => {
    if (me.error instanceof ApiError && me.error.status === 401) {
      clearKioskToken();
      clearAllClientState(queryClient);
      setToken(null);
    }
  }, [me.error, queryClient]);

  // Mark first user interaction — required for iOS AudioContext (CLAUDE.md §22).
  useEffect(() => {
    function onInteract(): void {
      markUserInteracted();
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('keydown', onInteract);
    }
    window.addEventListener('pointerdown', onInteract, { passive: true });
    window.addEventListener('keydown', onInteract);
    return () => {
      window.removeEventListener('pointerdown', onInteract);
      window.removeEventListener('keydown', onInteract);
    };
  }, []);

  const handleErrorDismiss = useCallback(() => setError(null), []);
  useEffect(() => {
    if (!error) return;
    const t = setTimeout(handleErrorDismiss, ERROR_OVERLAY_MS);
    return () => clearTimeout(t);
  }, [error, handleErrorDismiss]);

  const pushActivity = useCallback((entry: ActivityItem) => {
    setActivity((prev) => [entry, ...prev].slice(0, ACTIVITY_LIMIT));
  }, []);

  const handleDetected = useCallback(
    (text: string) => {
      // Local debounce: ignore the same code within 1.5s — avoids the
      // scanner firing twice for one badge.
      const t = Date.now();
      if (
        lastScanRef.current &&
        lastScanRef.current.text === text &&
        t - lastScanRef.current.at < 1500
      ) {
        return;
      }
      lastScanRef.current = { text, at: t };

      // Don't kick off a new scan while one is pending or feedback is up.
      if (scanMutation.isPending || result || error) return;

      // Optimistic cache lookup → instant in-column panel. Skipped if a real
      // result already arrived.
      void lookupBarcodeCache(text).then((entry) => {
        if (!entry) return;
        if (result) return;
        setOptimistic({
          fullName: entry.fullName,
          mode: isOnlineNow() ? 'online' : 'offline',
        });
      });

      // Offline path: persist to scan-queue, emit queue-changed event, do not
      // POST. The background-sync loop will drain it once we're online.
      if (!isOnlineNow()) {
        void enqueueOfflineScan(text)
          .then(() => notifyQueueChanged())
          .catch(() => {
            playSound('error');
            setError({
              kind: 'OTHER',
              message: 'Scan konnte nicht gespeichert werden.',
            });
          });
        return;
      }

      scanMutation.mutate(
        { barcode: text },
        {
          onSuccess: (data) => {
            setOptimistic(null);
            setResult(data);
            pushActivity({
              id: `${data.student.id}-${data.timestamp}`,
              firstName: data.student.firstName,
              lastName: data.student.lastName,
              className: data.student.className,
              time: formatClock(new Date(data.timestamp)),
              status: (data.status === ATTENDANCE_STATUS.VERSPAETET
                ? ATTENDANCE_STATUS.VERSPAETET
                : ATTENDANCE_STATUS.ANWESEND) as AttendanceStatus,
              action: data.action,
            });
            if (data.status === ATTENDANCE_STATUS.VERSPAETET) {
              toast.warning('Korrektur: verspätet markiert.', { duration: 3_000 });
            } else if (data.status === ATTENDANCE_STATUS.FEHLT) {
              toast.error('Korrektur: als fehlend markiert.', { duration: 3_000 });
            }
          },
          onError: (err) => {
            setOptimistic(null);
            if (err instanceof ApiError) {
              if (err.status === 401) {
                clearKioskToken();
                clearAllClientState(queryClient);
                setToken(null);
                return;
              }
              if (err.status === 404 && err.code === 'STUDENT_NOT_FOUND') {
                playSound('error');
                setError({
                  kind: 'STUDENT_NOT_FOUND',
                  message: 'Barcode nicht zugeordnet — bitte Lehrkraft ansprechen.',
                });
                return;
              }
              if (err.status === 429) {
                playSound('error');
                setError({
                  kind: 'RATE_LIMITED',
                  message: 'Zu viele Scans, bitte kurz warten.',
                });
                return;
              }
              if (err.status >= 500) {
                void enqueueOfflineScan(text)
                  .then(() => notifyQueueChanged())
                  .catch(() => {});
                playSound('error');
                setError({
                  kind: 'SERVER',
                  message: 'Scan erfasst — wird gleich übertragen.',
                });
                return;
              }
            }
            // Fallback: network error, AbortController-Timeout (10 s in
            // useScanMutation), JSON parse failures — anything that wasn't
            // an ApiError. Treat like 5xx: queue the scan so it isn't lost,
            // play the error sound for audible feedback, show an overlay
            // that communicates persistence rather than loss. Closes L1
            // from docs/investigations/kiosk-offline-behavior.md.
            void enqueueOfflineScan(text)
              .then(() => notifyQueueChanged())
              .catch(() => {});
            playSound('error');
            setError({
              kind: 'NETWORK',
              message: 'Verbindung verloren — Scan gespeichert, wird nachgereicht.',
            });
          },
        },
      );
    },
    [scanMutation, queryClient, result, error, pushActivity],
  );

  const dawdleThresholds = useMemo(
    () => ({
      warn: DEFAULT_DAWDLE_SETTINGS.dawdleWarnMinutes,
      alert: DEFAULT_DAWDLE_SETTINGS.dawdleAlertMinutes,
    }),
    [],
  );

  // Camera-frame feedback colour: derived from the active overlay state.
  const scannerFeedback = useMemo<ScannerFeedback | null>(() => {
    if (error) return { tone: 'error' };
    if (result) {
      if (result.action === 'check-out' || result.action === 'still-checked-out') {
        return { tone: 'logout' };
      }
      if (result.status === ATTENDANCE_STATUS.VERSPAETET) return { tone: 'late' };
      // Dawdle threshold also bumps to "late" tone — same colour as verspaetet.
      const dawdle = result.dawdleAtCheckin ?? 0;
      if (dawdle > 0) {
        const sev = classifyDawdle(dawdle, dawdleThresholds.warn, dawdleThresholds.alert);
        if (sev === 'orange' || sev === 'red') return { tone: 'late' };
      }
      return { tone: 'success' };
    }
    if (optimistic) {
      return optimistic.mode === 'offline' ? null : { tone: 'success' };
    }
    return null;
  }, [error, result, optimistic, dawdleThresholds.warn, dawdleThresholds.alert]);

  if (!token) {
    return <LoadingState variant="spinner" label="Weiterleitung …" />;
  }

  const me_ = me.data;
  const schoolName = settings.data?.schoolName ?? 'ELSA-Brändström-Gymnasium';
  const checkinTimeStr = result ? formatClock(new Date(result.timestamp)) : undefined;

  // Pause the scanner while any panel is up so the same badge isn't picked up
  // twice — the scanner re-arms once the panel auto-dismisses.
  const scannerPaused = Boolean(result) || Boolean(error) || Boolean(optimistic);

  return (
    <div
      className={cn(
        'flex h-screen min-h-0 flex-col overflow-hidden',
        // Subtler ELSA-teal-tinted gradient that fades into white below the
        // scanner stage. Header/footer have their own bg-card surface so the
        // gradient only shows through in the main area.
        'bg-[linear-gradient(180deg,hsl(186_60%_96%)_0%,hsl(0_0%_100%)_45%,hsl(0_0%_100%)_100%)]',
        // iOS PWA safe-area insets — viewport-fit=cover is already set globally
        // in index.html, so without these the iPad status bar sits on top of
        // the header.
        '[padding-top:env(safe-area-inset-top)] [padding-bottom:env(safe-area-inset-bottom)]',
      )}
    >
      {/* ───── Header ─────────────────────────────────────────────────── */}
      <header className="flex flex-none items-center justify-between border-b bg-card px-6 py-3 md:px-9 md:py-4">
        <div className="flex min-w-0 items-center gap-4">
          <img
            src="/brand/elsa-logo.png"
            alt={schoolName}
            className="h-10 w-auto shrink-0 md:h-12"
          />
          <div className="hidden h-9 w-px bg-border md:block" />
          <div className="min-w-0">
            <div className="truncate text-base font-semibold leading-tight md:text-lg">
              {me_?.roomName ?? '—'}
            </div>
            <div className="truncate text-xs text-muted-foreground md:text-sm">
              {schoolName}
              {me_?.kioskName ? <span className="ml-1">· {me_.kioskName}</span> : null}
            </div>
          </div>
        </div>

        <div className="flex shrink-0 flex-col items-end gap-1 text-xs text-muted-foreground md:text-sm">
          <div className="flex items-center gap-3 md:gap-4">
            <span className="flex items-center gap-1.5">
              <span
                aria-hidden
                className={cn(
                  'inline-block h-2 w-2 rounded-full',
                  online ? 'bg-status-present' : 'bg-status-absent',
                )}
              />
              {online ? 'Online' : 'Offline'}
            </span>
            <span aria-hidden>·</span>
            <span className="font-mono tabular-nums">{formatClock(now)}</span>
            <span aria-hidden className="hidden sm:inline">
              ·
            </span>
            <span className="hidden sm:inline">{formatDate(now)}</span>
          </div>
          {me_?.currentProjectDay ? (
            <PaCountdown
              date={me_.currentProjectDay.date}
              endTime={me_.currentProjectDay.endTime}
            />
          ) : null}
        </div>
      </header>

      {/* ───── Main 2-column area ─────────────────────────────────────── */}
      <main className="flex min-h-0 flex-1 items-stretch gap-6 px-6 py-4 md:gap-9 md:px-9">
        {/* Left: scanner stage — preview centred with status text directly
            beneath it. This replaces the previous 3-column layout where the
            preview was tucked to the side. */}
        <section className="flex min-w-0 flex-1 flex-col items-center justify-center gap-6 md:gap-8">
          <BarcodeScanner
            onDetected={handleDetected}
            paused={scannerPaused}
            frameVariant="large"
            feedback={scannerFeedback}
          />

          <div className="flex w-full max-w-xl flex-col items-center text-center">
            {error ? (
              <div
                role="alert"
                data-testid="kiosk-error-overlay"
                data-error-kind={error.kind}
                className="anim-slide-up flex w-full flex-col items-center gap-3"
              >
                <span className="inline-flex items-center gap-2 rounded-full bg-status-absent/15 px-3 py-1 text-sm font-semibold text-status-absent">
                  <span aria-hidden className="h-2 w-2 rounded-full bg-status-absent" />
                  Hinweis
                </span>
                <h1 className="text-3xl font-bold leading-tight md:text-4xl">{error.message}</h1>
              </div>
            ) : result ? (
              <ScanResultOverlay
                result={result}
                onDismiss={() => setResult(null)}
                dawdleWarnMinutes={dawdleThresholds.warn}
                dawdleAlertMinutes={dawdleThresholds.alert}
                checkinTime={checkinTimeStr}
              />
            ) : optimistic ? (
              <OptimisticOverlay
                fullName={optimistic.fullName}
                mode={optimistic.mode}
                onDismiss={() => setOptimistic(null)}
              />
            ) : scanMutation.isPending ? (
              <div className="flex w-full flex-col items-center gap-3">
                <h1 className="text-3xl font-bold text-primary md:text-4xl">Wird gescannt …</h1>
                <p className="text-base text-muted-foreground md:text-lg">
                  Bitte einen Moment Geduld.
                </p>
              </div>
            ) : (
              <div className="flex w-full flex-col items-center gap-3">
                <h1 className="text-3xl font-bold leading-[1.05] tracking-tight md:text-4xl">
                  Ausweis scannen
                </h1>
                <p className="text-base text-muted-foreground md:text-lg">
                  Halte deinen Schülerausweis vor die Kamera, um dich anzumelden.
                </p>
              </div>
            )}
          </div>
        </section>

        {/* Right: recent activity */}
        <ActivityColumn items={activity} />
      </main>

      {/* ───── Footer ──────────────────────────────────────────────────── */}
      <footer className="flex flex-none items-center justify-between border-t bg-card px-6 py-2 text-xs text-muted-foreground md:px-9 md:py-3 md:text-sm">
        <img
          src="/brand/wir-schaffen-zukunft.png"
          alt="Wir schaffen Zukunft"
          className="h-5 w-auto opacity-90 md:h-6"
        />
        <span>Probleme? Lehrkraft ansprechen.</span>
      </footer>

      <OfflineBanner />
    </div>
  );
}

function ActivityColumn({ items }: { items: ActivityItem[] }): JSX.Element {
  return (
    <aside className="hidden w-64 shrink-0 flex-col gap-2 md:flex" aria-label="Letzte Anmeldungen">
      <div className="flex items-center justify-between px-1 pt-1">
        <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
          Letzte Anmeldungen
        </span>
        <span className="text-xs text-muted-foreground">{items.length}</span>
      </div>
      <ul className="flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto pr-1">
        {items.length === 0 ? (
          <li className="rounded-lg border border-dashed bg-card/60 px-3 py-3 text-center text-xs text-muted-foreground">
            Noch keine Scans heute.
          </li>
        ) : (
          items.map((item) => {
            const tone = activityTone(item);
            return (
              <li
                key={item.id}
                className="flex items-center gap-3 rounded-lg border bg-card px-3 py-2 text-sm"
              >
                <span
                  aria-hidden
                  className={cn(
                    'grid h-7 w-7 shrink-0 place-items-center rounded-full text-[13px] font-semibold',
                    tone.iconBg,
                    tone.iconColor,
                  )}
                >
                  {tone.symbol}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {item.firstName} {item.lastName}
                  </span>
                  <span className="block text-xs text-muted-foreground">
                    Klasse {item.className}
                  </span>
                </span>
                <span className="shrink-0 font-mono text-xs tabular-nums text-muted-foreground">
                  {item.time}
                </span>
              </li>
            );
          })
        )}
      </ul>
    </aside>
  );
}
